import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';

import { selectCompanyLeadSourcesOptions } from 'store/companies/selectors';
import {
  selectCommonInfoLoanApplication,
  selectIsLoanLocked,
  selectLoanApplicationSubjectPropertyState,
} from 'store/loanApplications/selectors';
import { selectLoanLienPriorityOptions } from 'store/loanApplications/selectors/lienPriorities.selectors';
import { selectBuyDownsValues } from 'store/programs/selectors';
import {
  selectImpoundTypes,
  selectInvestorFeatureCodesOptions,
  selectProgramIdentifiersOptions,
} from 'store/system/selectors';

import {
  buildInsuranceEscrowIds,
  buildLoanAndLockContext,
  buildMortgageLienPriorityOptions,
} from '../LoanEditorCustomFormDrawer.loanAndLock.utils';

const useLoanAndLockContext = ({
  applicationData,
  liveValues,
  loanAndLockResponseData,
  housingFinanceAgenciesOptions,
  isLoanNoPpeEngineType,
  onEscrowMasterChange,
}) => {
  const programIdentifiersOptions = useSelector(selectProgramIdentifiersOptions);
  const investorFeatureCodesOptions = useSelector(selectInvestorFeatureCodesOptions);
  const leadSourcesOptions = useSelector(selectCompanyLeadSourcesOptions);
  const buyDownOptions = useSelector(selectBuyDownsValues, _.isEqual);
  const isLoanLocked = useSelector(selectIsLoanLocked);
  const lienPriorityOptions = useSelector(selectLoanLienPriorityOptions);
  const subjectPropertyState = useSelector(selectLoanApplicationSubjectPropertyState);
  const { escrowRequirement } = useSelector(selectCommonInfoLoanApplication) ?? {};
  const impoundTypes = useSelector(selectImpoundTypes);

  const mortgageLienPriorityOptions = useMemo(
    () =>
      buildMortgageLienPriorityOptions({
        additionalMortgages: applicationData.additionalMortgages,
        lienPriorityOptions,
      }),
    [applicationData.additionalMortgages, lienPriorityOptions],
  );

  const insuranceEscrowIds = useMemo(
    () => buildInsuranceEscrowIds(applicationData.escrowSettings),
    [applicationData.escrowSettings],
  );

  return useMemo(
    () =>
      buildLoanAndLockContext({
        isEnabled: !_.isNil(loanAndLockResponseData),
        applicationData,
        liveValues,
        subjectPropertyState,
        escrowRequirement,
        impoundTypes,
        onEscrowMasterChange,
        buyDownOptions,
        insuranceEscrowIds,
        isLoanLocked,
        isLoanNoPpeEngineType,
        housingFinanceAgenciesOptions,
        investorFeatureCodesOptions,
        leadSourcesOptions,
        mortgageLienPriorityOptions,
        programIdentifiersOptions,
      }),
    [
      applicationData,
      buyDownOptions,
      escrowRequirement,
      housingFinanceAgenciesOptions,
      impoundTypes,
      insuranceEscrowIds,
      investorFeatureCodesOptions,
      isLoanLocked,
      isLoanNoPpeEngineType,
      leadSourcesOptions,
      liveValues,
      loanAndLockResponseData,
      mortgageLienPriorityOptions,
      onEscrowMasterChange,
      programIdentifiersOptions,
      subjectPropertyState,
    ],
  );
};

export default useLoanAndLockContext;
